import { Box, Divider, Flex, Text, Textarea } from "@chakra-ui/react"
import { resolveLabel } from "@esgt/helpers"
import type { Question } from "@esgt/types"
import { MethodI18nFragment, MethodI18nText } from "components/I18nText"
import { useRating } from "lib/providers/RatingProvider"
import type React from "react"
import { useEffect, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { ShortTextInput } from "./ShortTextInput"
import { createRegexFromValidationPattern } from "./createRegexFromValidationPattern"

interface SourcedInputGroupRowsProps {
  question: Question
  isReadOnly: boolean
  saveChanges: () => void
}

/**
 * Renders repeatable groups of inputs whose rows are driven by an external
 * source — either another question's repeatable answers (via `questionId`)
 * or a fixed list of keys (via `staticKeys`).
 *
 * Each source row is rendered as a vertical section with a heading
 * (the resolved source value) and the remaining inputs stacked below.
 * Add/remove buttons are not shown — row management is handled by the source.
 */
export const SourcedInputGroupRows: React.FC<SourcedInputGroupRowsProps> = ({ question, isReadOnly, saveChanges }) => {
  const { ratingState, method } = useRating()
  const form = useFormContext()

  const { repeatableSource, textInputs } = question
  if (!repeatableSource || !textInputs?.length) return null

  const inputsPerGroup = textInputs.length

  // Detect which variant of repeatableSource we have
  const isStaticSource = "staticKeys" in repeatableSource

  // For questionId variant: look up the source question to know its inputs-per-group
  const sourceQuestion = useMemo(
    () => (!isStaticSource ? method?.config.questions.find((q) => q.id === repeatableSource.questionId) : undefined),
    [method, isStaticSource, repeatableSource],
  )

  const sourceInputsPerGroup = sourceQuestion?.textInputs?.length ?? 0
  const sourcedInput = textInputs[repeatableSource.inputIndex]

  // Extract source keys based on variant
  const sourceKeys = useMemo((): string[] => {
    if (isStaticSource) {
      return repeatableSource.staticKeys.map((sk) => sk.key)
    }

    // Existing logic for questionId variant
    if (!sourceInputsPerGroup) return []
    const sourceAnswers = ratingState?.answerValues[repeatableSource.questionId]
    if (!sourceAnswers) return []

    const maxIndex = Object.keys(sourceAnswers)
      .filter((k) => k.startsWith("input_"))
      .reduce((max, k) => Math.max(max, Number.parseInt(k.split("_")[1], 10) + 1), 0)
    const groupCount = Math.ceil(maxIndex / sourceInputsPerGroup)

    const keys: string[] = []
    for (let g = 0; g < groupCount; g++) {
      const key = sourceAnswers[`input_${g * sourceInputsPerGroup + repeatableSource.inputIndex}`]
      if (key && key !== "") {
        keys.push(key as string)
      }
    }
    return keys
  }, [isStaticSource, repeatableSource, ratingState?.answerValues, sourceInputsPerGroup])

  // Sync sourced column values into the form whenever source keys change.
  const { setValue, getValues } = form
  useEffect(() => {
    for (let g = 0; g < sourceKeys.length; g++) {
      const fieldName = `input_${g * inputsPerGroup + repeatableSource.inputIndex}`
      setValue(fieldName, sourceKeys[g])
    }

    // Clear leftover slots beyond the current source count.
    const formValues = getValues()
    const maxFormIndex = Object.keys(formValues)
      .filter((k) => k.startsWith("input_"))
      .reduce((max, k) => Math.max(max, Number.parseInt(k.split("_")[1], 10) + 1), 0)
    const expectedSlots = sourceKeys.length * inputsPerGroup

    for (let i = expectedSlots; i < maxFormIndex; i++) {
      setValue(`input_${i}`, "")
    }
  }, [sourceKeys, inputsPerGroup, repeatableSource.inputIndex, setValue, getValues])

  if (sourceKeys.length === 0) {
    return null
  }

  return (
    <Flex flexDirection="column" gap={6}>
      {sourceKeys.map((sourceKey, groupIndex) => {
        const staticKeyEntry = isStaticSource ? repeatableSource.staticKeys[groupIndex] : undefined

        return (
          <Box key={sourceKey}>
            {groupIndex > 0 && <Divider mb={4} />}
            <Text fontSize="14px" fontWeight={600} mb={3}>
              {staticKeyEntry ? (
                <MethodI18nFragment text={staticKeyEntry.label} />
              ) : sourcedInput?.selectSource ? (
                resolveLabel(sourcedInput.selectSource, sourceKey)
              ) : (
                sourceKey
              )}
            </Text>
            <Flex flexDirection="column" gap={3}>
              {textInputs.map((textInput, localIdx) => {
                if (localIdx === repeatableSource.inputIndex) return null
                const inputName = `input_${groupIndex * inputsPerGroup + localIdx}`

                return (
                  <Flex key={inputName} flexDirection="column">
                    {textInput.text && (
                      <Text fontSize="14px" mb={1} fontWeight={500} as="div">
                        <MethodI18nText text={textInput.text} />
                      </Text>
                    )}
                    {textInput.size === "lg" ? (
                      <Textarea
                        isDisabled={isReadOnly}
                        bgColor="white"
                        size="sm"
                        {...form.register(inputName, {
                          pattern: createRegexFromValidationPattern(textInput.validationPattern),
                        })}
                        onBlur={saveChanges}
                      />
                    ) : (
                      <ShortTextInput
                        questionInput={textInput}
                        disabled={isReadOnly}
                        inputName={inputName}
                        saveChanges={saveChanges}
                      />
                    )}
                    {textInput.helpText && (
                      <Text fontSize="14px" mt={1} color="gray.600" as="div">
                        <MethodI18nText text={textInput.helpText} />
                      </Text>
                    )}
                  </Flex>
                )
              })}
            </Flex>
          </Box>
        )
      })}
    </Flex>
  )
}
